import React from 'react';

import TruncatedCell from 'components/partials/table-component/truncated-cell/truncated-cell';
import Checkbox from 'components/partials/checkbox/checkbox';

import { FUND_TYPE_LABELS, FUND_SOURCE_LABELS } from '../funds-step.constants';

const commonHeaderStyles =
  'border-r-2 border-white font-semibold cursor-pointer';

type FundLike = { id: string | number; [key: string]: unknown };

type CellInfo = {
  renderValue: () => unknown;
  getValue: () => unknown;
  row: { original: FundLike };
};

type GetSelectFundsModalTableSettingsArgs = {
  selectedIds: Array<string | number>;
  onToggle: (fund: FundLike) => void;
  onToggleAll: () => void;
  allSelected: boolean;
  someSelected: boolean;
};

export const getSelectFundsModalTableSettings = ({
  selectedIds,
  onToggle,
  onToggleAll,
  allSelected,
  someSelected,
}: GetSelectFundsModalTableSettingsArgs) => [
  {
    id: 'checkbox',
    header: () => (
      <Checkbox
        labelFor="select-all-funds"
        checkboxValue="selectAll"
        checked={allSelected}
        indeterminate={someSelected && !allSelected}
        onChange={() => onToggleAll()}
      />
    ),
    cell: (info: CellInfo) => {
      const fund = info.row.original;
      const isSelected = selectedIds.includes(fund.id);

      return (
        <Checkbox
          labelFor={`fund-${fund.id}`}
          checkboxValue={String(fund.id)}
          checked={isSelected}
          onChange={() => onToggle(fund)}
        />
      );
    },
    headerClassName: `${commonHeaderStyles} rounded-tl-md rounded-bl-md`,
    className: 'w-[4%] py-2 px-3',
    enableSorting: false,
  },
  {
    id: 'name',
    accessorKey: 'name',
    header: () => <span>FUND NAME</span>,
    cell: (info: CellInfo) => (
      <TruncatedCell value={info.renderValue() as string} />
    ),
    headerClassName: commonHeaderStyles,
    className: 'w-[50%] text-left font-semibold pl-2.5 py-2 max-w-0',
    enableSorting: true,
    sortingFn: 'alphanumeric',
  },
  {
    id: 'code',
    accessorKey: 'code',
    header: () => <span>CODE</span>,
    cell: (info: CellInfo) => info.renderValue() as React.ReactNode,
    headerClassName: commonHeaderStyles,
    className: 'w-[10%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'source',
    accessorKey: 'source',
    header: () => <span>SOURCE</span>,
    cell: (info: CellInfo) => {
      const v = info.getValue() as keyof typeof FUND_SOURCE_LABELS;

      return (
        (FUND_SOURCE_LABELS as Record<string, string>)[v as string] ||
        (v as React.ReactNode)
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'type',
    accessorKey: 'type',
    header: () => <span>TYPE</span>,
    cell: (info: CellInfo) => {
      const v = info.getValue() as keyof typeof FUND_TYPE_LABELS;

      return (
        (FUND_TYPE_LABELS as Record<string, string>)[v as string] ||
        (v as React.ReactNode)
      );
    },
    headerClassName: commonHeaderStyles,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'instAid',
    accessorKey: 'instAid',
    header: () => <span>INST. AID</span>,
    cell: (info: CellInfo) => (info.getValue() ? 'Yes' : 'No'),
    headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
  {
    id: 'totalAid',
    accessorKey: 'totalAid',
    header: () => <span>TOTAL AID</span>,
    cell: (info: CellInfo) => (info.getValue() ? 'Yes' : 'No'),
    headerClassName: `${commonHeaderStyles} rounded-tr-md rounded-br-md`,
    className: 'w-[12%] pl-4 py-2',
    enableSorting: true,
  },
];
